import { ArrowRight, Laptop, Zap, Radio, Settings, Palette, Leaf, Trees, HardHat, Factory, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { programs } from '../data/programs';

// Maps each program slug to an icon
const programIcons: Record<string, React.ReactNode> = {
  informatique:       <Laptop className="w-16 h-16 text-gold opacity-40" />,
  'electricite-energies': <Zap className="w-16 h-16 text-gold opacity-40" />,
  'reseaux-telecoms': <Radio className="w-16 h-16 text-gold opacity-40" />,
  mecatronique:       <Settings className="w-16 h-16 text-gold opacity-40" />,
  'arts-numeriques':  <Palette className="w-16 h-16 text-gold opacity-40" />,
  agroforesterie:     <Leaf className="w-16 h-16 text-gold opacity-40" />,
  'sciences-bois-foret': <Trees className="w-16 h-16 text-gold opacity-40" />,
  'mines-exploration': <HardHat className="w-16 h-16 text-gold opacity-40" />,
  'mines-industries': <Factory className="w-16 h-16 text-gold opacity-40" />,
};

export default function ProgramGrid({ searchFilter = '' }: { searchFilter?: string }) {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language.split('-')[0] as 'en' | 'fr') || 'fr';

  const filteredPrograms = searchFilter
    ? programs.filter(p => {
        const data = p.translations[currentLang] || p.translations.fr;
        return data.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
               data.category.toLowerCase().includes(searchFilter.toLowerCase());
      })
    : programs;

  return (
    <div className="lg:w-3/4 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
      <div className="flex justify-between items-center mb-10 border-b border-gray-100 pb-4">
        <span className="text-sm font-black text-gray-400 uppercase tracking-[0.2em]">
          {filteredPrograms.length} {t('showing_count', 'Filières')}
        </span>
        <div className="flex items-center gap-3 text-sm font-black">
          <span className="text-gray-400 uppercase tracking-widest">{t('sort_by', 'Trier:')}</span>
          <select className="outline-none bg-transparent cursor-pointer hover:text-gold transition-colors uppercase tracking-widest text-xs">
            <option>{t('sort_alphabetical', 'Alphabétique')}</option>
            <option>{t('sort_credential', 'Niveau')}</option>
          </select>
        </div>
      </div>

      {filteredPrograms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {filteredPrograms.map((program) => {
            const data = program.translations[currentLang] || program.translations.fr;
            const icon = programIcons[program.slug];
            return (
              <div
                key={program.slug}
                className="group bg-white border border-gray-100 flex flex-col h-full shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 rounded-sm overflow-hidden"
              >
                {/* Media Area */}
                <div className={`h-48 overflow-hidden flex items-center justify-center bg-navy`}>
                  {program.heroImage ? (
                    <img
                      src={program.heroImage}
                      alt={data.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-80"
                      loading="lazy"
                    />
                  ) : (
                    icon
                  )}
                </div>

                {/* Content */}
                <div className="p-6 md:p-8 flex flex-col flex-1">
                  <div className="text-gold text-[10px] font-black uppercase tracking-[0.3em] mb-3">{data.category}</div>
                  <h3 className="text-lg font-black mb-3 group-hover:text-gold transition-colors leading-tight text-navy uppercase tracking-tight">
                    {data.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-8 leading-relaxed font-medium line-clamp-2">{data.description}</p>

                  <div className="mt-auto pt-5 border-t border-gray-50 flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic">{data.duration}</span>
                    <Link
                      to={`/programs/${program.slug}`}
                      className="bg-navy text-white text-xs font-black uppercase tracking-[0.15em] px-5 py-2.5 flex items-center gap-2 hover:bg-gold hover:text-navy transition-all active:scale-95 group/link"
                    >
                      {t('common_details', 'Détails')}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-20 text-center space-y-4 bg-gray-50 rounded-sm border-2 border-dashed border-gray-200">
          <Search className="w-12 h-12 text-gray-300 mx-auto" />
          <p className="text-gray-500 font-bold uppercase tracking-widest italic">{t('no_programs_found', 'Aucun programme trouvé.')}</p>
          <button onClick={() => (window.location.href = '/programs')} className="text-navy font-black underline hover:text-gold uppercase tracking-widest text-xs">
            {t('reset_filters', 'Réinitialiser')}
          </button>
        </div>
      )}
    </div>
  );
}
